import React, { ReactElement, useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { reusableCssClass } from '@/utils/reusableCssClasses'
import { getNotecard, getNotecardMeta } from '@/api/cb/contentCreatorContent'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { extractTextFromStringifiedEditorState } from 'magic-text-box-lexical/src/utils/extractTextFromStringifiedEditorState'
import { Spin } from 'antd'

interface NotecardIDsListProps {
    notecardIDs: string[]
}

interface NotecardDisplay {
    id: string
    frontText: string
}

export const NotecardIDsList: React.FC<NotecardIDsListProps> = (
    props
): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const setViewingState = useSetRecoilState(viewingStateAtom)
    const [notecardDisplays, setNotecardDisplays] = useState<NotecardDisplay[]>(
        []
    )
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const fetchNotecardFronts = async () => {
            setIsLoading(true)
            const displays: NotecardDisplay[] = []

            for (const notecardID of props.notecardIDs) {
                try {
                    const response = await getNotecard(courseName, notecardID)
                    const notecard = response.data.payload
                    const frontText = extractTextFromStringifiedEditorState(
                        notecard.front
                    )
                    displays.push({
                        id: notecardID,
                        frontText: frontText || '(empty)',
                    })
                } catch (error) {
                    // If we can't fetch the notecard, just show the ID
                    displays.push({
                        id: notecardID,
                        frontText: '(error loading)',
                    })
                }
            }

            setNotecardDisplays(displays)
            setIsLoading(false)
        }

        fetchNotecardFronts()
    }, [courseName, props.notecardIDs])

    const redirectToNotecardID = async (notecardID: string): Promise<void> => {
        const response = await getNotecardMeta(courseName, notecardID)
        setViewingState({
            contentPath: response.data.payload.contentPath,
            viewingStateType: ViewingStateType.notecards,
            id: notecardID,
        })
    }

    if (isLoading) {
        return (
            <div style={{ padding: '16px', textAlign: 'center' }}>
                <Spin size="small" />
            </div>
        )
    }

    return (
        <ul className={'notecard-ids-list'}>
            {notecardDisplays.map((display) => {
                return (
                    <li
                        key={display.id}
                        className={`notecard-ids-list-item ${reusableCssClass.clickableText}`}
                        onClick={() => redirectToNotecardID(display.id)}
                    >
                        <div>
                            <strong>{display.id}</strong>
                            {' - '}
                            {display.frontText}
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
